import { useState, useEffect } from "react";
import {
  Stack,
  Paper,
  Text,
  Group,
  Button,
  Select,
  Badge,
  Alert,
  Loader,
  Table,
  NumberFormatter,
} from "@mantine/core";
import { IconSearch, IconLink, IconUnlink, IconRefresh, IconPlugConnected, IconPlugConnectedX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { jsonGet, jsonPost, jsonDelete } from "../api";
import { useStore } from "../store";

export default function GscSettings({ brand }) {
  const [status, setStatus] = useState(null);
  const [sites, setSites] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [keywordSummary, setKeywordSummary] = useState(null);

  const isLinked = !!brand?.gscSiteUrl;

  useEffect(() => {
    // Check for post-OAuth redirect params
    const params = new URLSearchParams(window.location.search);
    if (params.get("gsc_connected") === "true") {
      notifications.show({
        title: "GSC connected",
        message: "Google Search Console has been connected to your account.",
        color: "green",
      });
    }
    if (params.get("gsc_error")) {
      notifications.show({
        title: "GSC connection failed",
        message: "Failed to connect Google Search Console. Please try again.",
        color: "red",
      });
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    jsonGet("google-search-console/status")
      .catch(() => ({ isConnected: false }))
      .then((statusData) => {
        setStatus(statusData);
        if (statusData?.isConnected) {
          return jsonGet("google-search-console/sites").catch(() => ({ sites: [] }));
        }
        return { sites: [] };
      })
      .then((sitesData) => {
        setSites(sitesData?.sites || []);
        setLoading(false);
      });

    // Load keyword summary if brand has GSC linked
    if (brand?.gscSiteUrl && brand?.id) {
      jsonGet(`google-search-console/brands/${brand.id}/keywords`)
        .then(setKeywordSummary)
        .catch(() => {});
    }
  }, [brand?.id, brand?.gscSiteUrl]);

  const authToken = useStore((state) => state.authToken);

  const handleConnect = () => {
    // Navigate to the OAuth endpoint — browser will redirect to Google
    // Pass auth token as query param since full-page navigation doesn't include Bearer header
    const returnUrl = encodeURIComponent(window.location.pathname + "?tab=search-console");
    window.location.href = `/api/v1/google-search-console/connect?returnUrl=${returnUrl}&token=${encodeURIComponent(authToken)}`;
  };

  const handleDisconnect = async () => {
    try {
      await jsonDelete("google-search-console/connection");
      notifications.show({ title: "GSC disconnected", color: "blue" });
      window.location.reload();
    } catch {
      notifications.show({ title: "Error", message: "Failed to disconnect", color: "red" });
    }
  };

  const handleLink = async () => {
    if (!selectedSite) return;
    setLinking(true);
    try {
      const result = await jsonPost(`google-search-console/brands/${brand.id}/link`, {
        siteUrl: selectedSite,
      });
      notifications.show({
        title: "GSC linked",
        message: `Linked and synced ${result.keywordsSynced || 0} keywords`,
        color: "green",
      });
      window.location.reload();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to link GSC site",
        color: "red",
      });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    try {
      await jsonDelete(`google-search-console/brands/${brand.id}/link`);
      notifications.show({ title: "GSC unlinked", color: "blue" });
      window.location.reload();
    } catch {
      notifications.show({ title: "Error", message: "Failed to unlink", color: "red" });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await jsonPost(`google-search-console/brands/${brand.id}/sync`);
      notifications.show({
        title: "Sync complete",
        message: `Synced ${result.keywordsSynced} keywords from GSC`,
        color: "green",
      });
    } catch (err) {
      notifications.show({ title: "Sync failed", message: err.message, color: "red" });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack mt="md" gap="md">
      {/* Tenant-level GSC connection */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600}>Google Search Console Connection</Text>
            <Badge color={status?.isConnected ? "green" : "gray"} variant="light">
              {status?.isConnected ? "Connected" : "Not connected"}
            </Badge>
          </Group>

          {!status?.isConnected && (
            <>
              <Text size="sm" c="dimmed">
                Connect your Google account to access Search Console data.
                This connection is shared across all brands in your workspace.
              </Text>
              <Button
                leftSection={<IconPlugConnected size={16} />}
                onClick={handleConnect}
              >
                Connect Google Search Console
              </Button>
            </>
          )}

          {status?.isConnected && (
            <Group>
              <Text size="sm" c="dimmed">
                Your workspace is connected to Google Search Console.
              </Text>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<IconPlugConnectedX size={14} />}
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>

      {/* Brand-level site linking (only when connected) */}
      {status?.isConnected && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Text fw={600}>Site Linking</Text>

            {isLinked && (
              <>
                <Group gap="sm">
                  <IconLink size={16} />
                  <Text size="sm">
                    Linked to <Text span fw={600}>{brand.gscSiteUrl.replace("sc-domain:", "")}</Text>
                  </Text>
                </Group>
                <Group>
                  <Button
                    variant="light"
                    leftSection={<IconRefresh size={16} />}
                    loading={syncing}
                    onClick={handleSync}
                  >
                    Sync Now
                  </Button>
                  <Button
                    variant="subtle"
                    color="red"
                    leftSection={<IconUnlink size={16} />}
                    onClick={handleUnlink}
                  >
                    Unlink
                  </Button>
                </Group>

                {keywordSummary && keywordSummary.totalKeywords > 0 && (
                  <>
                    <Group gap="xs">
                      <Badge variant="light" size="sm">
                        {keywordSummary.totalKeywords} keywords synced
                      </Badge>
                      {keywordSummary.lastSynced && (
                        <Text size="xs" c="dimmed">
                          Last synced {new Date(keywordSummary.lastSynced).toLocaleDateString()}
                        </Text>
                      )}
                    </Group>
                    <Table size="sm" tabularNums>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Top Keywords</Table.Th>
                          <Table.Th ta="right">Clicks</Table.Th>
                          <Table.Th ta="right">Impressions</Table.Th>
                          <Table.Th ta="right">Avg Position</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {keywordSummary.topKeywords.map((kw) => (
                          <Table.Tr key={kw.keyword}>
                            <Table.Td>{kw.keyword}</Table.Td>
                            <Table.Td ta="right">
                              <NumberFormatter value={kw.clicks} thousandSeparator />
                            </Table.Td>
                            <Table.Td ta="right">
                              <NumberFormatter value={kw.impressions} thousandSeparator />
                            </Table.Td>
                            <Table.Td ta="right">{kw.position > 0 ? kw.position.toFixed(1) : "—"}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </>
                )}
              </>
            )}

            {!isLinked && (
              <>
                <Text size="sm" c="dimmed">
                  Select a verified site to link to this brand. GSC data will sync weekly.
                </Text>
                <Group>
                  <Select
                    placeholder="Select a GSC site"
                    data={sites.map((s) => ({ value: s.siteUrl, label: s.siteUrl.replace("sc-domain:", "") }))}
                    value={selectedSite}
                    onChange={setSelectedSite}
                    style={{ flex: 1 }}
                    searchable
                  />
                  <Button
                    leftSection={<IconSearch size={16} />}
                    loading={linking}
                    disabled={!selectedSite}
                    onClick={handleLink}
                  >
                    Link & Sync
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
